// Policies — persisted on device; a manual add auto-creates an end-date
// reminder (the core P0 value loop: policy in wallet -> never miss a date).

import { Platform } from 'react-native';
import { create } from 'zustand';

import { fetchPolfinancePolicies, PolfinancePolicy } from '@/api/mobile-api';
import { getDb, TABLES } from '@/db';
import { useRemindersStore } from '@/store/reminders-store';
import type { Policy, PolicyType, Reminder } from '@/types/domain';
import { uuid } from '@/utils/format';

/** Copy a picked file (photo/PDF) into the app-private sandbox so the URI
 *  survives — a picker's cache URI is temporary. Web keeps the original URI. */
async function persistPolicyFile(sourceUri: string): Promise<string> {
  if (Platform.OS === 'web') return sourceUri;
  const { Directory, File, Paths } = await import('expo-file-system');
  const dir = new Directory(Paths.document, 'policies');
  if (!dir.exists) dir.create();
  const ext = sourceUri.includes('.') ? sourceUri.slice(sourceUri.lastIndexOf('.')) : '.jpg';
  const dest = new File(dir, `${uuid()}${ext}`);
  new File(sourceUri).copy(dest);
  return dest.uri;
}

export interface ManualPolicyInput {
  type: PolicyType;
  customName: string | null;
  insurerName: string;
  policyNumber: string | null;
  premiumGr: number;
  endDate: string | null; // null = open-ended
  nextTechnicalExamDate?: string | null; // motor policies only — feeds the TECH_EXAM reminder
  // Optional extras the AI scan can fill in beyond the quick manual path
  source?: 'MANUAL' | 'SCAN';
  subject?: string | null;
  startDate?: string | null;
  premiumPeriod?: Policy['premiumPeriod'];
}

/** Fields the detail screen lets you edit on a locally-held policy. */
export type PolicyEdit = Pick<
  Policy,
  | 'type'
  | 'customName'
  | 'insurerName'
  | 'policyNumber'
  | 'premiumGr'
  | 'startDate'
  | 'endDate'
  | 'openEnded'
  | 'nextTechnicalExamDate'
>;

interface PoliciesState {
  hydrated: boolean;
  policies: Policy[];
  /** Read-only Polfinance policies of the LINKED account (P2) — server-side, not persisted locally. */
  polfinancePolicies: PolfinancePolicy[];
  hydrate: () => Promise<void>;
  loadPolfinancePolicies: () => Promise<void>;
  addManualPolicy: (input: ManualPolicyInput) => Promise<Policy>;
  /** Edit a locally-held (MANUAL/SCAN) policy and re-sync its end-date reminder. */
  updatePolicy: (id: string, patch: Partial<PolicyEdit>) => Promise<void>;
  /** Attach / detach a locally-stored document (photo or PDF) to a policy. */
  addPolicyDocument: (id: string, uri: string) => Promise<void>;
  removePolicyDocument: (id: string, uri: string) => Promise<void>;
  /** Delete a locally-held policy and clean up its reminders (from the detail screen). */
  removePolicy: (id: string) => Promise<void>;
}

/** The end-date reminder a policy should have (null = open-ended, no reminder). */
function endDateReminderFor(policy: Policy): Reminder | null {
  if (!policy.endDate) return null;
  const now = new Date().toISOString();
  return {
    id: uuid(),
    policyId: policy.id,
    type: policy.type === 'CAR_OC' ? 'OC_END' : 'PROTECTION_END',
    title: policy.type === 'CAR_OC' ? 'Koniec OC' : 'Koniec ochrony',
    subtitle: policy.customName ?? policy.insurerName,
    eventDate: policy.endDate,
    leadDaysBefore: [30, 14, 7, 1],
    enabled: true,
    lastFiredAt: null,
    updatedAt: now,
  };
}

/** The technical-inspection reminder a motor policy should have (null = no date set).
 *  Only for LOCAL policies: linked Polfinance policies are reminded server-side
 *  (push/SMS from the CRM), so a local reminder would double-notify the client. */
function techExamReminderFor(policy: Policy): Reminder | null {
  if (!policy.nextTechnicalExamDate) return null;
  const now = new Date().toISOString();
  return {
    id: uuid(),
    policyId: policy.id,
    type: 'TECH_EXAM',
    title: 'Przegląd techniczny',
    subtitle: policy.customName ?? policy.insurerName,
    eventDate: policy.nextTechnicalExamDate,
    leadDaysBefore: [30, 14, 7, 1],
    enabled: true,
    lastFiredAt: null,
    updatedAt: now,
  };
}

export const usePoliciesStore = create<PoliciesState>((set, get) => ({
  hydrated: false,
  policies: [],
  polfinancePolicies: [],

  hydrate: async () => {
    const db = await getDb();
    const policies = (await db.list<Policy>(TABLES.policies)).sort((a, b) =>
      a.createdAt.localeCompare(b.createdAt),
    );
    set({ hydrated: true, policies });
  },

  loadPolfinancePolicies: async () => {
    // Best effort: offline or unlinked just leaves the section empty — the local
    // wallet is the source the app can always rely on.
    const result = await fetchPolfinancePolicies();
    if (result.ok && result.data) {
      set({ polfinancePolicies: result.data });
    }
  },

  addManualPolicy: async (input) => {
    const now = new Date().toISOString();
    const policy: Policy = {
      id: uuid(),
      source: input.source ?? 'MANUAL',
      systemInsuranceId: null,
      type: input.type,
      customName: input.customName,
      insurerName: input.insurerName,
      policyNumber: input.policyNumber,
      subject: input.subject ?? null,
      premiumGr: input.premiumGr,
      premiumPeriod: input.premiumPeriod ?? 'ANNUAL',
      startDate: input.startDate ?? null,
      endDate: input.endDate,
      openEnded: input.endDate === null,
      installments: [],
      nextTechnicalExamDate: input.nextTechnicalExamDate ?? null,
      documentUris: [],
      createdAt: now,
      updatedAt: now,
    };
    const db = await getDb();
    await db.upsert(TABLES.policies, policy.id, policy);
    set((s) => ({ policies: [...s.policies, policy] }));

    const reminder = endDateReminderFor(policy);
    if (reminder) await useRemindersStore.getState().addReminder(reminder);
    const techReminder = techExamReminderFor(policy);
    if (techReminder) await useRemindersStore.getState().addReminder(techReminder);
    return policy;
  },

  updatePolicy: async (id, patch) => {
    const now = new Date().toISOString();
    let updated: Policy | undefined;
    set((s) => ({
      policies: s.policies.map((p) => {
        if (p.id !== id) return p;
        updated = {
          ...p,
          ...patch,
          // Keep the two end fields consistent whichever one the form changed.
          openEnded: patch.openEnded ?? (patch.endDate === null ? true : patch.endDate ? false : p.openEnded),
          endDate: patch.openEnded ? null : (patch.endDate ?? p.endDate),
          updatedAt: now,
        };
        return updated;
      }),
    }));
    if (!updated) return;
    const db = await getDb();
    await db.upsert(TABLES.policies, updated.id, updated);
    // Rebuild the policy's reminders from scratch so changed/removed dates stay in sync.
    await useRemindersStore.getState().removeForPolicy(updated.id);
    const reminder = endDateReminderFor(updated);
    if (reminder) await useRemindersStore.getState().addReminder(reminder);
    const techReminder = techExamReminderFor(updated);
    if (techReminder) await useRemindersStore.getState().addReminder(techReminder);
  },

  addPolicyDocument: async (id, uri) => {
    const stored = await persistPolicyFile(uri);
    let updated: Policy | undefined;
    set((s) => ({
      policies: s.policies.map((p) => {
        if (p.id !== id || p.documentUris.includes(stored)) return p;
        updated = { ...p, documentUris: [...p.documentUris, stored], updatedAt: new Date().toISOString() };
        return updated;
      }),
    }));
    if (!updated) return;
    const db = await getDb();
    await db.upsert(TABLES.policies, updated.id, updated);
  },

  removePolicyDocument: async (id, uri) => {
    if (Platform.OS !== 'web') {
      try {
        const { File } = await import('expo-file-system');
        new File(uri).delete();
      } catch {
        // metadata cleanup proceeds even if the file is already gone
      }
    }
    let updated: Policy | undefined;
    set((s) => ({
      policies: s.policies.map((p) => {
        if (p.id !== id) return p;
        updated = { ...p, documentUris: p.documentUris.filter((u) => u !== uri), updatedAt: new Date().toISOString() };
        return updated;
      }),
    }));
    if (!updated) return;
    const db = await getDb();
    await db.upsert(TABLES.policies, updated.id, updated);
  },

  removePolicy: async (id) => {
    set((s) => ({ policies: s.policies.filter((p) => p.id !== id) }));
    const db = await getDb();
    await db.remove(TABLES.policies, id);
    await useRemindersStore.getState().removeForPolicy(id);
  },
}));
